import React, { useState } from 'react';
import { Box, Flex } from 'rebass/styled-components';
import { Radio } from '@getro/rombo';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { useUpdateTalentNetworkMutation } from 'services/network';
import { TalentDivider } from './talentDivider';
import { TalentSetting } from './components/talentSetting';

export const JobBoardTnSubscriptionSettings = ({ talentNetworkSettings }) => {
  const [values, setValues] = useState({
    askJbVisitorsToJoinTnBeforeLeavingToJobSource:
      talentNetworkSettings?.askJbVisitorsToJoinTnBeforeLeavingToJobSource ?? false,
    requireJbVisitorsToJoinTnBeforeLeavingToJobSource:
      talentNetworkSettings?.requireJbVisitorsToJoinTnBeforeLeavingToJobSource ?? false,
    askJobAlertSubscribersToJoinTn: talentNetworkSettings?.askJobAlertSubscribersToJoinTn ?? false,
    showJoinTnBlockToJobApplicants: talentNetworkSettings?.showJoinTnBlockToJobApplicants ?? false,
  });

  const [updateTalentNetwork] = useUpdateTalentNetworkMutation();

  const update = async (updates) => {
    try {
      await updateTalentNetwork(updates).unwrap();
      toast('Changes saved successfully', { icon: 'success' });
    } catch (err) {
      const msg = err?.data?.message || 'Failed to save changes';
      toast(msg, { icon: 'error' });
    }
  };

  const setValueForField = (field, value) => {
    setValues({ ...values, [field]: value });
    update({ ...values, [field]: value });
  };

  return (
    <>
      <Flex sx={{ flexDirection: 'column', gap: '40px' }}>
        <TalentSetting
          title="Capture job board visitor information"
          description="Turn anonymous visitors into enriched contacts. Understand your audience, measure the hires you help make, and grow your candidate database."
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <TalentSetting
            toggleTalentNetwork={() => {
              setValueForField(
                'askJbVisitorsToJoinTnBeforeLeavingToJobSource',
                !values.askJbVisitorsToJoinTnBeforeLeavingToJobSource,
              );
            }}
            tnHidden={values.askJbVisitorsToJoinTnBeforeLeavingToJobSource}
            title="Ask for LinkedIn profile before visiting a job posting"
            description="Prompt visitors for their LinkedIn profile before they leave to the company’s careers page."
          />
          {values.askJbVisitorsToJoinTnBeforeLeavingToJobSource && (
            <Flex sx={{ gap: '16px' }}>
              <Radio
                name="jbProfileRequirement"
                checked={values.requireJbVisitorsToJoinTnBeforeLeavingToJobSource}
                label="Required: Users must provide a profile"
                onChange={() => {
                  setValueForField('requireJbVisitorsToJoinTnBeforeLeavingToJobSource', true);
                }}
              />
              <Radio
                name="jbProfileRequirement"
                checked={!values.requireJbVisitorsToJoinTnBeforeLeavingToJobSource}
                label="Optional: Users can skip"
                onChange={() => {
                  setValueForField('requireJbVisitorsToJoinTnBeforeLeavingToJobSource', false);
                }}
              />
            </Flex>
          )}
        </Box>
        <TalentSetting
          toggleTalentNetwork={() => {
            setValueForField('askJobAlertSubscribersToJoinTn', !values.askJobAlertSubscribersToJoinTn);
          }}
          tnHidden={values.askJobAlertSubscribersToJoinTn}
          title="Invite job alert subscribers to join the Talent Network"
          description="Turn job alert subscribers into engaged candidates you can track and support."
        />
        <TalentSetting
          disabled={talentNetworkSettings.tnHidden}
          toggleTalentNetwork={() => {
            setValueForField('showJoinTnBlockToJobApplicants', !values.showJoinTnBlockToJobApplicants);
          }}
          tnHidden={values.showJoinTnBlockToJobApplicants && !talentNetworkSettings.tnHidden}
          title="Invite job applicants to complete their Talent Network profile"
          description="Show a message after users apply to a job, inviting them to complete their Talent Network profile. Only works when the Talent Network is enabled."
        />
      </Flex>
      <TalentDivider />
    </>
  );
};

JobBoardTnSubscriptionSettings.propTypes = {
  talentNetworkSettings: PropTypes.object.isRequired,
};
